import React, { createContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface SnackbarData {
  message?: string;
  severity?: 'success' | 'error' | 'warning' | 'info';
  /** Optional React node rendered as the snackbar's leading icon. */
  leadingIcon?: ReactNode;
  [key: string]: unknown;
}

interface SnackbarContextValue {
  snackbarData: SnackbarData;
  snackbarId: string | null;
  snackbarIsOpen: boolean;
  snackbarPosition: string;
  resetSnackbar: () => void;
  resetSnackbarData: () => void;
  setSnackbarData: (data: SnackbarData) => void;
  setSnackbarId: (id: string | null) => void;
  setSnackbarIsOpen: (isOpen: boolean) => void;
  setSnackbarPosition: (position: string) => void;
  updateSnackbarData: (data: Partial<SnackbarData>) => void;
}

const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

const SNACKBAR_POSITION_BOTTOM_LEFT = 'bottom-left';
const initialSnackbarIsOpen = false;
const initialSnackbarId = null;
const initialSnackbarData: SnackbarData = {};
const initialSnackbarPosition = SNACKBAR_POSITION_BOTTOM_LEFT;

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [snackbarIsOpen, setSnackbarIsOpen] = useState(initialSnackbarIsOpen);
  const [snackbarId, setSnackbarId] = useState<string | null>(initialSnackbarId);
  const [snackbarData, setSnackbarData] = useState<SnackbarData>(initialSnackbarData);
  const [snackbarPosition, setSnackbarPosition] = useState(initialSnackbarPosition);

  const resetSnackbarData = useCallback(
    () => setSnackbarData(initialSnackbarData),
    [],
  );

  const updateSnackbarData = useCallback(
    (data: Partial<SnackbarData> = {}) => {
      setSnackbarData((prevSnackbarData) => ({
        ...prevSnackbarData,
        ...data,
      }));
    },
    [],
  );

  const resetSnackbar = useCallback(() => {
    setSnackbarIsOpen(initialSnackbarIsOpen);
    setSnackbarId(initialSnackbarId);
    setSnackbarData(initialSnackbarData);
    setSnackbarPosition(initialSnackbarPosition);
  }, []);

  const value = useMemo(
    () => ({
      snackbarData,
      snackbarId,
      snackbarIsOpen,
      snackbarPosition,
      resetSnackbar,
      resetSnackbarData,
      setSnackbarData,
      setSnackbarId,
      setSnackbarIsOpen,
      setSnackbarPosition,
      updateSnackbarData,
    }),
    [
      snackbarData,
      snackbarId,
      snackbarIsOpen,
      snackbarPosition,
      resetSnackbar,
      resetSnackbarData,
      updateSnackbarData,
    ],
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
    </SnackbarContext.Provider>
  );
};

export default SnackbarContext;
